import React, { useMemo, useState, useEffect } from 'react';
import { Client, Order } from '../../types';
import { ImageIcon, XIcon, ChevronRightIcon, ArrowLeftIcon, UserIcon } from '../icons';
import { Image } from '../Image';

type GalleryFilter = 'all' | 'measurement' | 'client';
type GalleryImageType = 'measurement' | 'client' | 'order';

interface GalleryViewProps {
  clients: Client[];
  orders?: Order[];
  onSelectImage?: (url: string, clientName: string, clientId: string) => void;
}

export const GalleryView: React.FC<GalleryViewProps> = ({ clients, orders = [], onSelectImage }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [filter, setFilter] = useState<GalleryFilter>('all');

  const allImages = useMemo(() => {
    const images: { url: string; clientId: string; clientName: string; type: GalleryImageType }[] = [];

    clients.forEach((client) => {
      client.measurements.forEach((measurement) => {
        if (measurement.isDeleted) return;
        (measurement.attachments || []).forEach((url) => {
          images.push({ url, clientId: client.id, clientName: client.name, type: 'measurement' });
        });
      });
    });

    clients.forEach((client) => {
      (client.images || []).forEach((url) => {
        images.push({ url, clientId: client.id, clientName: client.name, type: 'client' });
      });
    });

    orders.forEach((order) => {
      if (order.isDeleted) return;
      const client = clients.find((c) => c.id === order.clientId);
      if (!client) return;
      (order.attachments || []).forEach((url) => {
        images.push({ url, clientId: client.id, clientName: client.name, type: 'order' });
      });
    });

    return Array.from(new Map(images.map((item) => [`${item.type}:${item.url}`, item])).values());
  }, [clients, orders]);

  const filteredImages = useMemo(() => {
    if (filter === 'all') return allImages;
    if (filter === 'measurement') return allImages.filter((img) => img.type === 'measurement');
    return allImages.filter((img) => img.type === 'client');
  }, [allImages, filter]);

  const sourceLabel = (type: GalleryImageType): string => {
    if (type === 'measurement') return 'Mesure';
    if (type === 'client') return 'Client';
    return 'Commande';
  };

  useEffect(() => {
    setSelectedIndex(null);
  }, [filter]);

  const showNext = () => {
    if (selectedIndex !== null && filteredImages.length > 0) {
      setSelectedIndex((selectedIndex + 1) % filteredImages.length);
    }
  };

  const showPrev = () => {
    if (selectedIndex !== null && filteredImages.length > 0) {
      setSelectedIndex((selectedIndex - 1 + filteredImages.length) % filteredImages.length);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') setSelectedIndex(null);
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'ArrowLeft') showPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, filteredImages.length]);

  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (diff > 50) showNext();
    if (diff < -50) showPrev();
    setTouchStart(null);
  };

  if (allImages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <ImageIcon className="w-10 h-10 text-gray-300" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">Galerie vide</h3>
        <p className="text-sm text-gray-500 max-w-xs mt-2">
          Les photos de tenues et clients que vous ajoutez apparaitront ici.
        </p>
      </div>
    );
  }

  const selectedImage = selectedIndex !== null ? filteredImages[selectedIndex] : null;

  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark">Galeries</h2>
          <p className="text-sm text-gray-500">{filteredImages.length} photos</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Tous
        </button>
        <button
          type="button"
          onClick={() => setFilter('measurement')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            filter === 'measurement' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Mesures
        </button>
        <button
          type="button"
          onClick={() => setFilter('client')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            filter === 'client' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Clients
        </button>
      </div>

      {filteredImages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          Aucune image pour ce filtre.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filteredImages.map((img, index) => (
            <div
              key={`${img.type}-${img.clientId}-${index}`}
              className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-sm active:scale-95 transition-transform cursor-pointer"
              onClick={() => setSelectedIndex(index)}
            >
              <Image src={img.url} alt={`Tenue de ${img.clientName}`} className="w-full h-full object-cover" />
              <div className="absolute left-2 top-2">
                <span className="inline-flex rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {sourceLabel(img.type)}
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                <p className="text-[10px] text-white font-medium truncate">{img.clientName}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center select-none overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="absolute top-0 inset-x-0 h-20 px-6 flex items-center justify-between bg-gradient-to-b from-black/50 to-transparent z-10">
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg">{selectedImage.clientName}</span>
              <span className="text-white/80 text-xs font-medium">{sourceLabel(selectedImage.type)}</span>
              <span className="text-white/60 text-xs">
                {(selectedIndex as number) + 1} / {filteredImages.length}
              </span>
            </div>
            <button
              onClick={() => setSelectedIndex(null)}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="relative w-full h-full flex items-center justify-center px-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              className="absolute left-4 p-4 rounded-full bg-white/5 hover:bg-white/10 text-white hidden md:flex items-center justify-center transition-all active:scale-90"
            >
              <ArrowLeftIcon className="w-8 h-8" />
            </button>

            <Image
              src={selectedImage.url}
              alt="Apercu plein ecran"
              className="max-w-full max-h-[75vh] object-contain shadow-2xl rounded-sm transition-opacity duration-300"
            />

            <button
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              className="absolute right-4 p-4 rounded-full bg-white/5 hover:bg-white/10 text-white hidden md:flex items-center justify-center transition-all active:scale-90"
            >
              <ChevronRightIcon className="w-8 h-8" />
            </button>
          </div>

          <div className="absolute bottom-10 inset-x-0 px-6 flex flex-col items-center gap-4">
            <button
              onClick={() => {
                onSelectImage?.(selectedImage.url, selectedImage.clientName, selectedImage.clientId);
                setSelectedIndex(null);
              }}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full font-bold shadow-lg flex items-center gap-2 transform active:scale-95 transition-all text-sm"
            >
              <UserIcon className="w-5 h-5" />
              Voir Profil Client
            </button>
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-bold">Glissez pour naviguer</p>
          </div>
        </div>
      )}
    </div>
  );
};
